import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getCountryFlag, getStatusColor, getStatusLabel } from '../utils/geo';
import { Activity, Wifi, ChevronRight } from 'lucide-react';

function HopRow({ hop, index, isLatest }) {
  const isFiltered = hop.filtered || hop.status === 'filtered';
  const isTimeout = hop.isTimeout || false;
  const statusColor = isFiltered ? '#555' : getStatusColor(hop.status);
  const flag = isFiltered ? '\u2022' : getCountryFlag(hop.country_code);
  const statusLabel = isFiltered ? 'NO REPLY' : getStatusLabel(hop.status);

  return (
    <motion.div
      data-testid="hop-row"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.03 }}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
        isFiltered ? 'opacity-40' : isLatest ? 'bg-zinc-200/50 dark:bg-white/8 border border-zinc-300 dark:border-white/10' : 'hover:bg-zinc-100 dark:hover:bg-white/5'
      }`}
      style={isLatest ? { borderColor: `${statusColor}30` } : {}}
    >
      {/* Hop number */}
      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-zinc-200 dark:bg-white/5 text-xs font-mono text-zinc-700 dark:text-white/50 shrink-0">
        {hop.hop_number}
      </div>

      {/* Flag + Country */}
      <div className="flex items-center gap-1.5 w-12 shrink-0">
        <span className="text-base">{flag}</span>
        <span className="text-[10px] font-mono text-zinc-500 dark:text-white/40 uppercase">{isFiltered ? '--' : hop.country_code}</span>
      </div>

      {/* IP + ISP */}
      <div className="flex-1 min-w-0">
        <div className="font-mono text-xs text-zinc-900 dark:text-white/80 truncate" data-testid="hop-ip">
          {isFiltered ? '* * *' : hop.ip}
        </div>
        <div className="text-[10px] text-zinc-600 dark:text-white/40 truncate">
          {isFiltered ? 'Request timed out' : hop.isp}
        </div>
      </div>

      {/* Latency */}
      <div className="text-right shrink-0" data-testid="hop-latency">
        <span className="font-mono text-xs font-medium" style={{ color: statusColor }}>
          {isFiltered ? '--' : hop.latency_ms != null ? `${hop.latency_ms}ms` : '--'}
        </span>
      </div>

      {/* Packet loss */}
      <div className="w-14 text-right shrink-0" data-testid="hop-packet-loss">
        {isFiltered ? (
          <span className="font-mono text-[10px] text-white/20">100%</span>
        ) : hop.packet_loss > 0 ? (
          <span className="font-mono text-[10px] text-[#ff3b3b]">
            {hop.packet_loss}% loss
          </span>
        ) : (
          <span className="font-mono text-[10px] text-white/20">0%</span>
        )}
      </div>

      {/* Status badge */}
      <div
        className="px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider shrink-0"
        style={{
          backgroundColor: `${statusColor}15`,
          color: statusColor,
          border: `1px solid ${statusColor}30`,
        }}
      >
        {statusLabel}
      </div>
    </motion.div>
  );
}

export default function HopPanel({ hops, allHops, traceState, traceInfo, traceSummary, showTimeoutHops, onToggleTimeoutHops }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [hops]);

  const isTracing = traceState === 'tracing';

  return (
    <div data-testid="hop-telemetry-panel" className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-300 dark:border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-cyan-600 dark:text-[#00f0ff]" />
            <h3 className="text-sm font-semibold tracking-wider uppercase text-zinc-700 dark:text-white/70">Hop Telemetry</h3>
          </div>
          <div className="flex items-center gap-2">
            {isTracing && (
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-emerald-600 dark:bg-[#00ff9d] animate-pulse" />
                <span className="text-[10px] font-mono text-emerald-700 dark:text-[#00ff9d] uppercase tracking-wider">Live</span>
              </div>
            )}
            {traceState === 'complete' && (
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-cyan-600 dark:bg-[#00f0ff]" />
                <span className="text-[10px] font-mono text-cyan-700 dark:text-[#00f0ff] uppercase tracking-wider">Real Data</span>
              </div>
            )}
          </div>
        </div>

        {traceInfo && (
          <div className="flex items-center gap-2 mt-2 text-xs text-zinc-600 dark:text-white/50">
            <span className="font-mono">{traceInfo.source_city || traceInfo.source_ip}</span>
            <ChevronRight className="w-3 h-3" />
            <span className="font-mono">{traceInfo.target_city || traceInfo.target}</span>
          </div>
        )}

        {/* Toggle Hidden Hops Button */}
        {traceState === 'complete' && traceSummary && (
          <button
            onClick={traceSummary.timeout_hops > 0 ? onToggleTimeoutHops : null}
            disabled={traceSummary.timeout_hops === 0}
            className={`w-full mt-3 px-3 py-2 border border-zinc-300 dark:border-white/10 rounded-lg transition-colors text-left ${
              traceSummary.timeout_hops > 0 
                ? 'bg-zinc-100 dark:bg-white/5 hover:bg-zinc-200 dark:hover:bg-white/10 cursor-pointer' 
                : 'bg-zinc-50 dark:bg-white/[0.02] cursor-default opacity-60'
            }`}
            data-testid="toggle-timeout-hops"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${
                  traceSummary.timeout_hops === 0 
                    ? 'bg-emerald-600 dark:bg-emerald-500' 
                    : showTimeoutHops 
                      ? 'bg-[#fbbf24]' 
                      : 'bg-zinc-400 dark:bg-white/30'
                }`} />
                <span className="text-xs font-mono text-zinc-700 dark:text-white/70">
                  {traceSummary.timeout_hops === 0 
                    ? 'No Hidden Hops' 
                    : showTimeoutHops 
                      ? 'Hide Hidden Hops' 
                      : 'Show Hidden Hops'}
                </span>
              </div>
              <span className="text-xs font-mono text-zinc-500 dark:text-white/40">
                {traceSummary.timeout_hops} filtered
              </span>
            </div>
          </button>
        )}
      </div>

      {/* Hop list */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
        <AnimatePresence>
          {hops.map((hop, idx) => (
            <HopRow
              key={hop.hop_number}
              hop={hop}
              index={idx}
              isLatest={idx === hops.length - 1 && isTracing}
            />
          ))}
        </AnimatePresence>

        {hops.length === 0 && traceState === 'idle' && (
          <div className="flex flex-col items-center justify-center h-48 text-zinc-500 dark:text-white/30">
            <Wifi className="w-8 h-8 mb-3" />
            <p className="text-sm">Waiting for trace to start...</p>
            <p className="text-xs mt-1">Route will auto-trace on load</p>
          </div>
        )}

        {traceState === 'connecting' && (
          <div className="flex flex-col items-center justify-center h-48 text-zinc-600 dark:text-white/40">
            <div className="w-8 h-8 border-2 border-[#00f0ff]/30 dark:border-[#00f0ff]/30 border-t-[#00f0ff] dark:border-t-[#00f0ff] rounded-full animate-spin mb-3" />
            <p className="text-sm">Connecting...</p>
          </div>
        )}

        {isTracing && hops.length === 0 && (
          <div className="flex flex-col items-center justify-center h-48 text-zinc-600 dark:text-white/40">
            <div className="w-8 h-8 border-2 border-[#00ff9d]/30 dark:border-[#00ff9d]/30 border-t-[#00ff9d] dark:border-t-[#00ff9d] rounded-full animate-spin mb-3" />
            <p className="text-sm">Connecting...</p>
            <p className="text-xs mt-1 text-zinc-500 dark:text-white/30">Waiting for first hop...</p>
          </div>
        )}

        {isTracing && hops.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 text-zinc-500 dark:text-white/30">
            <div className="w-4 h-4 border-2 border-[#00ff9d]/30 dark:border-[#00ff9d]/30 border-t-[#00ff9d] dark:border-t-[#00ff9d] rounded-full animate-spin" />
            <span className="text-xs font-mono">Running traceroute...</span>
          </div>
        )}
      </div>

      {/* Summary footer */}
      {traceSummary && (
        <div className="px-4 py-3 border-t border-zinc-300 dark:border-white/10 bg-zinc-100 dark:bg-zinc-900/80">
          <div className="grid grid-cols-4 gap-3">
            <div className="text-center">
              <div className="text-lg font-semibold text-cyan-700 dark:text-[#00f0ff]">{traceSummary.total_hops}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-600 dark:text-white/40">Total</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-emerald-700 dark:text-[#00ff9d]">{traceSummary.responding_hops || traceSummary.total_hops}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-600 dark:text-white/40">Responding</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-emerald-700 dark:text-[#00ff9d]">{traceSummary.avg_latency_ms}ms</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-600 dark:text-white/40">Avg RTT</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-zinc-500 dark:text-zinc-500">
                {traceSummary.filtered_hops || 0}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-600 dark:text-white/40">Filtered</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
